import type { MixedItemTag } from "@memorang/types";

export type ProethicaTestKind = "pretest" | "posttest";

// Chrome + recipe config for the pre/post tests. collectionId is deployment
// config (which collection backs each test, same category as projectId); every
// other value here is presentation. Live status/numItems come from `exams`.
export type ProethicaTestDescriptor = {
	kind: ProethicaTestKind;
	collectionId: number;
	anchorModuleNumber: number;
	title: string;
	subtitle: string;
	icon: string;
	typeLabel: string;
};

export const proethicaTestDescriptors: ProethicaTestDescriptor[] = [
	{
		kind: "pretest",
		collectionId: 799,
		anchorModuleNumber: 1,
		title: "Pre-Test",
		subtitle: "Check your baseline before you begin.",
		icon: "clipboard-text-outline",
		typeLabel: "Pre-Test",
	},
	{
		kind: "posttest",
		collectionId: 800,
		anchorModuleNumber: 8,
		title: "Post-Test",
		subtitle: "Measure how far you've come.",
		icon: "clipboard-check-outline",
		typeLabel: "Post-Test",
	},
];

const testByKind: Record<ProethicaTestKind, ProethicaTestDescriptor> =
	Object.fromEntries(
		proethicaTestDescriptors.map((test) => [test.kind, test]),
	) as Record<ProethicaTestKind, ProethicaTestDescriptor>;

const testByCollectionId: Record<number, ProethicaTestDescriptor> =
	Object.fromEntries(
		proethicaTestDescriptors.map((test) => [test.collectionId, test]),
	);

export const getProethicaTestByKind = (
	kind?: string,
): ProethicaTestDescriptor | undefined =>
	kind && kind in testByKind
		? testByKind[kind as ProethicaTestKind]
		: undefined;

// The dashboard keys a test row on its collectionId (now the module identity);
// reverse-map to the descriptor for chrome + kind.
export const getProethicaTestByCollectionId = (
	collectionId?: number | string,
): ProethicaTestDescriptor | undefined => {
	if (collectionId == null) {
		return undefined;
	}
	return testByCollectionId[Number(collectionId)];
};

// A ProEthica identity is a test iff it resolves to a test collection (the
// dashboard/session pass the collectionId) or, at the report boundary, a kind.
export const isProethicaTestId = (id?: string | number): boolean =>
	id != null && (Number(id) in testByCollectionId || String(id) in testByKind);

// Reviewing the pre-test would reveal the shared question pool before the
// post-test measures it, so the review-entry seams refuse pre-test sessions.
export const canEnterProethicaReview = (kind?: ProethicaTestKind): boolean =>
	kind !== "pretest";

export const getProethicaModuleNumber = (
	tag: MixedItemTag,
): number | undefined => {
	const match = (tag.label || tag.title || "").match(/Module\s+(\d+)/i);
	return match ? Number(match[1]) : undefined;
};
